import { useContext } from 'react';
import CommandCenterService from '../../../service/CommandCenterService';
import { showToastMessage } from '../../../utils/commonFunctions';
import { ConsoleFilterOptionType } from '../types';
import { CommandCenterStateProviderContext } from '../context';
import useUrlParams from './useUrlParams';

/**
 * useFilters
 * Hook that provides helpers to load and manage console filter options and groups.
 * Relies on CommandCenterStateProviderContext for shared state.
 */
const useFilters = () => {
    const context = useContext(CommandCenterStateProviderContext);
    if (context === undefined) {
        throw new Error('useSatellite must be used within a Satellite Provider');
    }

    const { scenario_id } = useUrlParams();

    const {
        consoleFilterOptions,
        setConsoleFilterOptions,
        setConsoleFilterGroups,
        consoleFilterGroups,
        setOriginalFilterMetadata,
        originalFilterMetadata,
        referenceIdConsole,
        setReferenceIdConsole,
    } = context;

    //Fetch filter options for a given combination of IDs.
    const getFilterOptions = async (
        truetwin_id,
        scenario_id,
        sat_id,
        mission_id
    ) => {
        try {
            const res = await CommandCenterService.getCommandFilterOptions(
                truetwin_id,
                scenario_id,
                sat_id,
                mission_id
            );
            if (res?.data?.data) {
                setConsoleFilterOptions(res.data.data[0]);
            }
        } catch (err: any) {
            showToastMessage('error', err?.response?.data?.message || 'Failed to get filter options');
        }
    };

    //Fetch all filter groups for a scenario and cache them in context as a Map.
    const getFilterGroups = async (scenario_id) => {
        try {
            const res = await CommandCenterService.getCommandFilterGroups(scenario_id);
            if (res?.data?.data) {
                const filterGroupsMap = new Map<string, ConsoleFilterOptionType>(
                    res.data.data.map((group: any) => [group.filterGroupId, group])
                );
                setConsoleFilterGroups(filterGroupsMap);
                return filterGroupsMap;
            }
        } catch (err: any) {
            showToastMessage('error', err?.response?.data?.message || 'Failed to get filter groups');
        }
    };

    //Create or update filter groups on the server.
    const patchFilterGroup = async (action, payload, onSuccess, requestId?: string) => {
        try {
            const requestPayload = {
                requestId: requestId ?? referenceIdConsole,
                filterGroupData: Array.isArray(payload) ? payload : [payload],
            };

            const res = await CommandCenterService.patchFilterGroups(scenario_id, requestPayload);

            if (res?.data) {
                onSuccess && onSuccess();
                if (action) showToastMessage('success', `Filter group ${action === 'create' ? 'created' : 'updated'} successfully`);
                return;
            }
        } catch (err: any) {
            showToastMessage('error', err?.response?.data?.message ?? `Failed to ${action ?? 'update'} filter group`);
        }
    };

    //Delete a filter group by id.
    const deleteFilterGroup = async (filterGroupId) => {
        try {
            const res = await CommandCenterService.deleteFilterGroup(scenario_id, filterGroupId, referenceIdConsole);

            if (res?.data) {
                showToastMessage('success', `Filter group deleted successfully`);
                setConsoleFilterGroups((prev) => {
                    const newMap = new Map(prev);
                    newMap.delete(filterGroupId);
                    return newMap;
                });
                return;
            }
        } catch (err: any) {
            showToastMessage('error', err?.response?.data?.message || `Failed to delete filter group`);
        }
    };

    return {
        consoleFilterOptions,
        getFilterOptions,
        getFilterGroups,
        consoleFilterGroups,
        setConsoleFilterGroups,
        setOriginalFilterMetadata,
        patchFilterGroup,
        originalFilterMetadata,
        deleteFilterGroup,
        referenceIdConsole,
        setReferenceIdConsole,
    };
};

export default useFilters;